/* ============================================================
   Mega Menu — Phase 2
   ============================================================
   Pairs [data-mega-trigger="<id>"] (top-level buttons in header)
   with [data-mega-id="<id>"] (mega-panels rendered as sibling
   sections inside header-group). Modern-style hover-to-open +
   tab-switching inside panels.

   - 100ms open delay (hover-intent)
   - 220ms close delay (gives user time to reach panel)
   - Cross-switch instant when another mega is already open
   - ESC closes
   - Click outside closes
   - Click on top-level link still navigates to its URL
   - --header-bottom-position-desktop CSS var anchors panels under
     the (possibly sticky) header
   ============================================================ */

(function () {
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 220;

  let currentlyOpen = null;
  let openTimer = null;
  let closeTimer = null;

  function getHeaderWrapper() {
    return document.querySelector('.header-wrapper, sticky-header');
  }

  function updateHeaderPosition() {
    const hw = getHeaderWrapper();
    if (!hw) return;
    const bottom = Math.floor(hw.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--header-bottom-position-desktop', bottom + 'px');
  }

  function findPanel(id) {
    return document.querySelector('[data-mega-id="' + CSS.escape(id) + '"]');
  }

  /* Footer-cells live in a separate flex container from the columns
     they should sit under, so CSS alone can't 1:1 align them when the
     column widths are content-driven. We measure each column and
     mirror its width to the corresponding footer-cell (incl. empty
     cells that hold the slot under a column without footer_link). */
  function alignFooterCellsToColumns(panel) {
    if (!panel) return;
    const cols = panel.querySelectorAll('.mega-menu__column');
    const cells = panel.querySelectorAll('.mega-menu__footer-cell');
    if (!cols.length || cells.length !== cols.length) return;
    cols.forEach((col, i) => {
      const w = col.getBoundingClientRect().width;
      if (w > 0) cells[i].style.flex = '0 0 ' + w + 'px';
    });
  }

  function openPanel(panel) {
    if (!panel) return;
    if (currentlyOpen && currentlyOpen !== panel) {
      currentlyOpen.classList.remove('is-open');
      currentlyOpen.setAttribute('hidden', '');
    }
    panel.removeAttribute('hidden');
    // Force reflow so transition kicks in
    void panel.offsetHeight;
    panel.classList.add('is-open');
    currentlyOpen = panel;
    // Columns are now laid out — align footer cells to match
    requestAnimationFrame(() => alignFooterCellsToColumns(panel));

    // Mark matching trigger as active
    document.querySelectorAll('[data-mega-trigger]').forEach((t) => {
      t.classList.toggle('is-mega-active', t.getAttribute('data-mega-trigger') === panel.dataset.megaId);
    });
    updateHeaderPosition();
  }

  function closePanel(panel) {
    if (!panel) return;
    panel.classList.remove('is-open');
    // Hide after transition to remove from a11y tree
    setTimeout(() => {
      if (!panel.classList.contains('is-open')) {
        panel.setAttribute('hidden', '');
      }
    }, 220);
    if (currentlyOpen === panel) currentlyOpen = null;
    document.querySelectorAll('[data-mega-trigger].is-mega-active').forEach((t) => {
      t.classList.remove('is-mega-active');
    });
  }

  function closeAll() {
    document.querySelectorAll('.mega-panel.is-open').forEach(closePanel);
  }

  function scheduleOpen(panel, instant) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (!panel || panel === currentlyOpen) return;
    if (instant) {
      openPanel(panel);
    } else {
      openTimer = setTimeout(() => openPanel(panel), OPEN_DELAY);
    }
  }

  function scheduleClose(panel) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => closePanel(panel), CLOSE_DELAY);
  }

  function initTriggers() {
    const triggers = document.querySelectorAll('[data-mega-trigger]');
    triggers.forEach((trigger) => {
      const id = trigger.getAttribute('data-mega-trigger');
      const panel = findPanel(id);
      if (!panel) return; // disabled / direct-link / missing section

      trigger.addEventListener('mouseenter', () => {
        const instant = currentlyOpen && currentlyOpen !== panel;
        scheduleOpen(panel, instant);
      });

      trigger.addEventListener('mouseleave', () => {
        scheduleClose(panel);
      });

      // Cancel close if user moves into the panel
      panel.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
      });

      panel.addEventListener('mouseleave', () => {
        scheduleClose(panel);
      });

      // Keyboard: focus opens, blur (after delay) closes
      trigger.addEventListener('focus', () => scheduleOpen(panel, true));
    });
  }

  function initTabs() {
    const tabContainers = document.querySelectorAll('[data-mega-tabs]');
    tabContainers.forEach((container) => {
      const tabs = container.querySelectorAll('.mega-menu__tab[data-tab-target]');
      const panels = container.querySelectorAll('.mega-menu__tab-panel[data-tab-content]');

      tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const target = tab.getAttribute('data-tab-target');

          tabs.forEach((t) => {
            const active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });

          panels.forEach((p) => {
            const active = p.getAttribute('data-tab-content') === target;
            p.classList.toggle('is-active', active);
            if (active) {
              p.removeAttribute('hidden');
            } else {
              p.setAttribute('hidden', '');
            }
          });
        });

        tab.addEventListener('mouseenter', () => {
          // hover-to-switch on tab list — same logic as click
          tab.click();
        });
      });
    });
  }

  function initGlobalHandlers() {
    // ESC closes any open mega
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && currentlyOpen) {
        closePanel(currentlyOpen);
      }
    });

    // Click outside closes
    document.addEventListener('click', (e) => {
      if (!currentlyOpen) return;
      if (e.target.closest('.mega-panel') || e.target.closest('[data-mega-trigger]')) return;
      closePanel(currentlyOpen);
    });

    // Reposition on scroll/resize. Resize also re-aligns footer-cells
    // because column widths can change with viewport.
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        updateHeaderPosition();
        raf = null;
      });
    };
    let resizeRaf = null;
    const onResize = () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        updateHeaderPosition();
        if (currentlyOpen) alignFooterCellsToColumns(currentlyOpen);
        resizeRaf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
  }

  function init() {
    updateHeaderPosition();
    initTriggers();
    initTabs();
    initGlobalHandlers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
